import { Input, Loading } from '@nike/frame-component-library';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactSelect from 'react-select';

import { formatStoreAlias, sortStoresByNumber } from '../../utils/formatting';
import { storeViewsByCountry } from '../../utils/service-calls/reusable';
import countries from '../../utils/static/countriesWithNikeStores';
import { ButtonWhite } from '../reusable';

import { storeMatchesSearch } from './helpers';

const STORE_FIELDS = ['id', 'storeNumber', 'name', 'address.country', 'facilityType', 'storeStatus'];

const SEARCH_PLACEHOLDER = 'Search by store number, name, or comma-separated store numbers';

const StoreMultiSelect = ({
  isDisabled,
  selectedCountry,
  selectedStores,
  onChangeCountry,
  onChangeSelected,
}) => {
  const [storesInCountry, setStoresInCountry] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [availableSearch, setAvailableSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    if (!selectedCountry || !selectedCountry.value) {
      setStoresInCountry([]);
      return () => { cancelled = true; };
    }
    setFetching(true);
    setFetchError('');
    (async () => {
      try {
        const res = await storeViewsByCountry(selectedCountry.value, false, STORE_FIELDS);
        if (cancelled) return;
        if (!res.body || !Array.isArray(res.body.objects)) {
          throw new Error('Unexpected response from store views');
        }
        const open = res.body.objects.filter((s) => s.storeStatus === 'OPEN');
        sortStoresByNumber(open);
        setStoresInCountry(open);
      } catch (err) {
        if (cancelled) return;
        setStoresInCountry([]);
        setFetchError(err.message || 'Failed to load stores for this country');
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCountry]);

  const selectedIdSet = new Set(selectedStores.map((s) => s.id));
  const availableStores = storesInCountry.filter((s) => !selectedIdSet.has(s.id) && storeMatchesSearch(s, availableSearch));
  const visibleSelected = selectedStores.filter((s) => storeMatchesSearch(s, selectedSearch));

  const addStore = (store) => {
    if (selectedIdSet.has(store.id)) return;
    onChangeSelected([...selectedStores, store]);
  };

  const removeStore = (storeId) => {
    onChangeSelected(selectedStores.filter((s) => s.id !== storeId));
  };

  const selectAllFiltered = () => {
    const merged = [...selectedStores];
    const seen = new Set(merged.map((s) => s.id));
    availableStores.forEach((s) => {
      if (!seen.has(s.id)) {
        merged.push(s);
        seen.add(s.id);
      }
    });
    onChangeSelected(merged);
  };

  const clearSelected = () => onChangeSelected([]);

  return (
    <section className="o2o-bulk-multiselect">
      <article className="o2o-bulk-multiselect-country">
        <label className="o2o-bulk-field-label" htmlFor="o2o-bulk-country">Country</label>
        <ReactSelect
          classNamePrefix="react-select"
          clearable={false}
          id="o2o-bulk-country"
          isDisabled={isDisabled}
          options={countries}
          placeholder="Select a country"
          styles={{
            container: (s) => ({ ...s, zIndex: 110 }),
            control: (s) => ({
              ...s, borderColor: 'lightgrey', padding: '0px 0px 0px 3px',
            }),
          }}
          value={selectedCountry}
          onChange={onChangeCountry}
        />
      </article>

      {!selectedCountry && (
        <p className="o2o-bulk-empty-hint">Select a country to load stores.</p>
      )}

      {selectedCountry && fetching && (
        <div className="o2o-bulk-loading"><Loading /></div>
      )}

      {selectedCountry && !fetching && fetchError && (
        <aside className="o2o-bulk-error">{fetchError}</aside>
      )}

      {selectedCountry && !fetching && !fetchError && (
        <div className="o2o-bulk-pane-grid">
          <article className="o2o-bulk-pane-col">
            <div className="o2o-bulk-pane-header">
              <strong>{`Available (${availableStores.length})`}</strong>
              <ButtonWhite
                className="border o2o-bulk-pane-action-btn"
                isDisabled={isDisabled || availableStores.length === 0}
                label="Select All Filtered"
                onClick={selectAllFiltered}
              />
            </div>
            <Input
              id="o2o-bulk-available-search"
              label=""
              placeholder={SEARCH_PLACEHOLDER}
              type="text"
              value={availableSearch}
              onChange={({ target: { value } }) => setAvailableSearch(value)}
            />
            <div className="o2o-bulk-pane mt2-sm">
              {availableStores.length === 0 ? (
                <div className="o2o-bulk-pane-empty">No matching stores</div>
              ) : (
                availableStores.map((store) => (
                  <div
                    key={store.id}
                    className="o2o-bulk-pane-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => !isDisabled && addStore(store)}
                    onKeyDown={(e) => {
                      if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) addStore(store);
                    }}
                  >
                    {`${formatStoreAlias(store)} - ${store.name}${store.facilityType ? ` - ${store.facilityType}` : ''}`}
                  </div>
                ))
              )}
            </div>
          </article>

          <article className="o2o-bulk-pane-col">
            <div className="o2o-bulk-pane-header">
              <strong>{`Selected (${selectedStores.length})`}</strong>
              <ButtonWhite
                className="border o2o-bulk-pane-action-btn"
                isDisabled={isDisabled || selectedStores.length === 0}
                label="Clear Selected"
                onClick={clearSelected}
              />
            </div>
            <Input
              id="o2o-bulk-selected-search"
              label=""
              placeholder={SEARCH_PLACEHOLDER}
              type="text"
              value={selectedSearch}
              onChange={({ target: { value } }) => setSelectedSearch(value)}
            />
            <div className="o2o-bulk-pane mt2-sm">
              {visibleSelected.length === 0 ? (
                <div className="o2o-bulk-pane-empty">No stores selected</div>
              ) : (
                visibleSelected.map((store) => (
                  <div
                    key={store.id}
                    className="o2o-bulk-pane-row is-selected"
                    role="button"
                    tabIndex={0}
                    onClick={() => !isDisabled && removeStore(store.id)}
                    onKeyDown={(e) => {
                      if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) removeStore(store.id);
                    }}
                  >
                    {`✕  ${formatStoreAlias(store)} - ${store.name}`}
                  </div>
                ))
              )}
            </div>
          </article>
        </div>
      )}
    </section>
  );
};

StoreMultiSelect.defaultProps = {
  isDisabled: false,
  selectedCountry: null,
};

StoreMultiSelect.propTypes = {
  isDisabled: PropTypes.bool,

  onChangeCountry: PropTypes.func.isRequired,

onChangeSelected: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
selectedCountry: PropTypes.object,
  selectedStores: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    storeNumber: PropTypes.string,
  })).isRequired,
};

export default StoreMultiSelect;
